import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import { Dropdown } from 'primereact/dropdown'
import Button from '../../components/ui/Button'
import RefreshButton from '../../components/ui/RefreshButton'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode } from '../../api/apiError'
import ProxyUserPicker from './components/ProxyUserPicker'
import { proxyUserLabel } from './components/proxyUserLabel'
import { userProxyColumns } from './components/UserProxyColumns'
import UserProxyFormDialog from './components/UserProxyFormDialog'
import { useUserProxies } from './hooks/useUserProxies'
import type { ProxyUserOption, UserProxy, UserProxyCreate } from './types'
import './admin.css'

/** Varsayılan sayfa boyutu (anayasa: tüm listelerde 10). */
const PAGE_SIZE = 10

/**
 * Durum filtresinin "Tümü" sentinel'i. Boş-OLMAYAN bir değer gerekir: PrimeReact Dropdown,
 * `null`/`''` değerli seçeneği "boş seçim" sayıp `onChange`'i yutar (078/045'te belgelenmiştir).
 * `RECORD_STATUS` daima `>= 0` olduğundan negatif değer gerçek bir durumla çakışamaz; API'ye giderken
 * `null`'a (filtresiz) çevrilir.
 */
const ALL_STATUSES = -1

/**
 * Admin → **Vekalet** (090). `USERS_PROXY` üzerinde kim kime vekalet verdiğini yönetir: sayfalı liste +
 * üç filtre + yeni kayıt + "Vekaleti Kaldır".
 *
 * **Filtreler (kullanıcı isteği 2026-08-19):** üstte iki kişi alanı — *Vekalet Veren* ve *Vekalet
 * Verilen* — ad/soyad/sicil ile aranır; yanlarında `RECORD_STATUS` filtresi durur ve **açılış değeri
 * "Aktif"tir**. Üçü **VE** ile birleşir ve **herhangi biri değişince sayfa 1'e döner** — aksi hâlde
 * kullanıcı daralttığı sonuç kümesinin var olmayan 4. sayfasında boş bir tablo görürdü (078 deseni).
 *
 * Açılışta **tüm aktif liste** gelir (kişi filtresi boş, durum = Aktif); filtreler yalnız daraltır.
 *
 * **"Vekaleti Kaldır" silme DEĞİL, pasifleştirmedir** (`RECORD_STATUS = 0` + `REVOKE_DATE`): kayıt
 * durum filtresi "Pasif"/"Tümü" iken görünmeye devam eder. Eylem geri alınamaz → anayasa gereği **onay
 * adımı** vardır.
 *
 * **Düzenleme yoktur**: bir vekaletin tarafları değiştirilemez (yanlış kayıt kaldırılır, doğrusu
 * eklenir) — aksi hâlde `GRANT_DATE` hangi çiftin ne zaman yetkilendirildiğini anlatmaz olurdu.
 *
 * Yetki: **`USER_PROXY_MANAGE`** — ekranın tüm işlemleri (okuma dahil) tek anahtara bağlıdır (kullanıcı
 * kararı). Menü ve rota da aynı koda bakar; asıl koruma sunucudadır.
 */
export default function UserProxiesAdminPage() {
  const { t } = useTranslation()
  const toast = useToast()

  const [from, setFrom] = useState<ProxyUserOption | null>(null)
  const [to, setTo] = useState<ProxyUserOption | null>(null)
  // Açılış varsayılanı **Aktif** (kullanıcı isteği). Sunucu tarafında varsayılan YOKTUR: parametrenin
  // hiç gönderilmemesi "Tümü" demektir, yani varsayılan uca gömülürse "Tümü" ifade edilemez olurdu.
  const [recordStatus, setRecordStatus] = useState<number>(1)
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(PAGE_SIZE)

  const [formOpen, setFormOpen] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  /** Kaldırma isteği süren satır — yalnız o satırın butonu kilitlenir (078 deseni). */
  const [busyId, setBusyId] = useState<number | null>(null)

  const filters = useMemo(
    () => ({
      fromUserId: from?.id ?? null,
      toUserId: to?.id ?? null,
      recordStatus: recordStatus === ALL_STATUSES ? null : recordStatus,
    }),
    [from, to, recordStatus],
  )

  const { list, create, revoke } = useUserProxies({
    ...filters,
    page: Math.floor(first / rows),
    size: rows,
  })

  const items = list.data?.content ?? []
  const total = list.data?.page.totalElements ?? 0

  /** "Tümü" listenin BAŞINDA, istemcide üretilir; 0/1 değerleri anayasada sabittir (RecordStatus). */
  const statusOptions = useMemo(
    () => [
      { label: t('admin.userProxies.filter.statusAll'), value: ALL_STATUSES },
      { label: t('admin.userProxies.status.active'), value: 1 },
      { label: t('admin.userProxies.status.passive'), value: 0 },
    ],
    [t],
  )

  /** Her filtre değişimi ilk sayfaya döner. */
  const onFilterChange = (apply: () => void) => {
    apply()
    setFirst(0)
  }

  const hasFilter = from != null || to != null || recordStatus !== 1

  /** "Temizle" ekranı **açılış** durumuna döndürür (durum = Aktif), boş filtreye değil. */
  const clearAll = () => {
    setFrom(null)
    setTo(null)
    setRecordStatus(1)
    setFirst(0)
  }

  const onSubmit = (payload: UserProxyCreate) => {
    create.mutate(payload, {
      onSuccess: () => {
        toast.show({
          severity: 'success',
          summary: t('admin.userProxies.toast.created'),
          life: 3000,
        })
        setFormOpen(false)
      },
      onError: (err) => {
        // Aynı çift için zaten aktif bir vekalet var → formda kalıp nedeni söyle (409).
        // Diğer hatalar (400 alan hatası, ağ) merkezî toast'a (019) düşer.
        if (getApiErrorCode(err) === 'INVALID_STATE') {
          setFormError(t('admin.userProxies.errors.duplicate'))
        }
      },
    })
  }

  /**
   * "Vekaleti Kaldır" — geri alınamaz (kayıt Pasif olur ve `REVOKE_DATE` yazılır) → **onay adımı**.
   * Onay metni iki tarafı da yazar, böylece kullanıcı yanlış satıra bastığını fark eder.
   */
  const onRevoke = (row: UserProxy) => {
    const unknown = t('admin.userProxies.unknownUser')
    confirmDialog({
      header: t('admin.userProxies.revoke.title'),
      message: t('admin.userProxies.revoke.confirm', {
        from: proxyUserLabel(row.fromName, row.fromSurname) || unknown,
        to: proxyUserLabel(row.toName, row.toSurname) || unknown,
      }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('admin.userProxies.revoke.accept'),
      accept: () => {
        setBusyId(row.id)
        // Damga satırdan gelir; bayatsa uç 409 verir ve merkezî hata yönetimi (019) kullanıcıya
        // "kayıt başkası tarafından güncellendi" der — liste tazelenince buton yeniden denenebilir.
        revoke.mutate(
          { id: row.id, modDate: row.modDate },
          {
            onSuccess: () =>
              toast.show({
                severity: 'success',
                summary: t('admin.userProxies.toast.revoked'),
                life: 3000,
              }),
            onSettled: () => setBusyId(null),
          },
        )
      },
    })
  }

  return (
    <div className="admin-page admin-user-proxies">
      <ConfirmDialog />
      <UserProxyFormDialog
        visible={formOpen}
        submitting={create.isPending}
        serverError={formError}
        onClearServerError={() => setFormError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.admin.userProxies')}
        actions={
          <>
            {/* Yenile, "Yeni"nin yaninda (kullanici istegi 2026-08-26). */}
            <RefreshButton onRefresh={() => list.refetch()} />
            <Button
              icon="pi pi-plus"
              label={t('admin.userProxies.actions.new')}
              onClick={() => {
                setFormError(null)
                setFormOpen(true)
              }}
            />
          </>
        }
      />

      {/*
        Arama kutusu (`onSearch`) verilMEZ: bu ekranda serbest metin araması yoktur — kişi araması
        seçicilerin İÇİNDEDİR ve listeye kimlik olarak gider. Ayrı bir arama kutusu, aynı işi yapan
        ikinci (ve farklı davranan) bir yol olurdu.

        Bu yüzden `controlsAlign="left"`: iki kişi seçici SOLDA durur (kullanıcı isteği
        2026-08-26). Arama bu ekranda seçicilerin içinde olduğundan, tasarımın "arama solda"
        rolünü onlar üstlenir; varsayılan (filtreler sağda) bu tek ekranda bilinçle ezilir.
      */}
      {/* `showClear` VERİLMEZ: FilterBar, Temizle'yi filtrelerin başına koyar ve bu ekranda o,
          sola alınmış kişi seçicilerinin de soluna, satırın en başına düşüyordu — temizlediği
          dropdown ise satırın öteki ucundaydı (kullanıcı bildirimi 2026-08-27).
          Buton bu yüzden aşağıda, dropdown'la AYNI sağ grupta elle çizilir; görünümü paylaşılan
          `.ui-filterbar-clear` sınıfından gelir, ikinci bir stil tanımlanmaz. */}
      <FilterBar controlsAlign="left">
        <div className="admin-filters-row">
          <ProxyUserPicker
            value={from}
            onChange={(u) => onFilterChange(() => setFrom(u))}
            placeholder={t('admin.userProxies.filter.fromPlaceholder')}
            ariaLabel={t('admin.userProxies.filter.fromLabel')}
          />
          <ProxyUserPicker
            value={to}
            onChange={(u) => onFilterChange(() => setTo(u))}
            placeholder={t('admin.userProxies.filter.toPlaceholder')}
            ariaLabel={t('admin.userProxies.filter.toLabel')}
          />
          {/* Sağ grup: Temizle + durum süzgeci. `margin-left: auto` artık BU sarmalayıcıda
              (eskiden dropdown'ın kendisindeydi) → ikisi birlikte sağa gider ve buton temizlediği
              kontrolün hemen solunda kalır. */}
          <span className="admin-proxy-right">
            {hasFilter && (
              <Button
                type="button"
                text
                label={t('admin.userProxies.filter.clear')}
                className="ui-filterbar-clear"
                onClick={clearAll}
              />
            )}
            <Dropdown
              value={recordStatus}
              options={statusOptions}
              onChange={(e) =>
                onFilterChange(() => setRecordStatus((e.value as number | null) ?? ALL_STATUSES))
              }
              aria-label={t('admin.userProxies.filter.statusLabel')}
              className="admin-proxy-status"
            />
          </span>
        </div>
      </FilterBar>

      {/* Sunucu-taraflı sayfalama (lazy). Kolon sıralaması kapalı → sortField/onSort verilmez. */}
      <DataTable
        value={items}
        loading={list.isLoading}
        lazy
        first={first}
        rows={rows}
        totalRecords={total}
        onPage={(e) => {
          setFirst(e.first)
          setRows(e.rows)
        }}
        emptyMessage={
          <EmptyState
            icon="pi pi-users"
            title={
              hasFilter
                ? t('admin.userProxies.emptyFiltered.title')
                : t('admin.userProxies.empty.title')
            }
            description={
              hasFilter
                ? t('admin.userProxies.emptyFiltered.desc')
                : t('admin.userProxies.empty.desc')
            }
          />
        }
      >
        {userProxyColumns({ t, busyId, onRevoke })}
      </DataTable>
    </div>
  )
}
